import React, { useEffect, useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { oapi } from '@/lib/officialsApi';
import { SettingsCard, useSaver } from './SettingsCard';
import { CrewSettings } from './CrewSettings';
import { JoinLinkCard, SchedulersCard } from './SchedulersCard';
import { AddonStatusCard } from './AddonStatusCard';
import { PaymentPermsCard } from './PaymentPermsCard';

const RolesCard = ({ roles, save }) => {
  const [rows, setRows] = useState(roles);
  const [busy, run] = useSaver(save);
  useEffect(() => setRows(roles), [roles]);
  const setLabel = (i, label) => setRows(r => r.map((x, j) => (j === i ? { ...x, label } : x)));
  return (
    <SettingsCard title="Official roles" desc="The positions you assign. Renaming keeps existing assignments." testId="settings-roles">
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.key || `new-${i}`} className="flex gap-2">
            <Input value={r.label} onChange={e => setLabel(i, e.target.value)} className="h-9" data-testid={`role-label-${i}`} />
            <Button variant="ghost" size="sm" className="h-9 text-slate-400 hover:text-red-600" onClick={() => setRows(x => x.filter((_, j) => j !== i))} data-testid={`role-remove-${i}`}><X className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-3">
        <Button variant="ghost" size="sm" onClick={() => setRows(x => [...x, { label: '' }])} data-testid="role-add-btn"><Plus className="w-4 h-4 mr-1" />Add role</Button>
        <Button size="sm" disabled={busy} onClick={() => run({ roles: rows.filter(r => r.label.trim()) }, 'Roles saved')} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="roles-save-btn">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save roles
        </Button>
      </div>
    </SettingsCard>
  );
};

const PRESETS = [[10080, '7 days'], [2880, '2 days'], [1440, '24 hours'], [360, '6 hours'], [180, '3 hours'], [60, '1 hour']];

const TimingCard = ({ s, save }) => {
  const [dur, setDur] = useState(s.game_duration_minutes);
  const [buf, setBuf] = useState(s.travel_buffer_minutes);
  const [enabled, setEnabled] = useState(s.reminders?.enabled !== false);
  const [offs, setOffs] = useState(s.reminders?.offsets_minutes || []);
  const [busy, run] = useSaver(save);
  const toggle = (m) => setOffs(o => (o.includes(m) ? o.filter(x => x !== m) : o.length >= 5 ? o : [...o, m]));
  const body = { game_duration_minutes: Number(dur), travel_buffer_minutes: Number(buf), reminders: { enabled, offsets_minutes: offs } };
  return (
    <SettingsCard title="Timing & reminders" desc="Used for conflict checks and automatic reminder emails." testId="settings-timing">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Game length (min)</Label><Input type="number" min={15} max={600} value={dur} onChange={e => setDur(e.target.value)} data-testid="settings-duration" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Travel buffer (min)</Label><Input type="number" min={0} max={480} value={buf} onChange={e => setBuf(e.target.value)} data-testid="settings-buffer" /></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700 mt-4"><Switch checked={enabled} onCheckedChange={setEnabled} data-testid="settings-reminders-switch" />Send reminder emails before each game</label>
      <div className={`flex flex-wrap gap-1.5 mt-3 ${enabled ? '' : 'opacity-40 pointer-events-none'}`}>
        {PRESETS.map(([m, l]) => (
          <button key={m} type="button" onClick={() => toggle(m)} data-testid={`reminder-offset-${m}`}
            className={`px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${offs.includes(m) ? 'bg-[#00247D] border-[#00247D] text-white' : 'bg-white text-slate-600'}`}>{l} before</button>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <Button size="sm" disabled={busy} onClick={() => run(body, 'Timing saved · reminders updated')} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="timing-save-btn">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save
        </Button>
      </div>
    </SettingsCard>
  );
};

export const SettingsTab = ({ orgId, role, data, onSaved }) => {
  const save = async (body, msg = 'Settings saved') => {
    const r = await oapi('/officials/settings', { method: 'PUT', orgId, body });
    onSaved(r);
    toast.success(msg);
  };
  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="officials-settings-tab">
      <div className="lg:col-span-7 space-y-6">
        <CrewSettings data={data} save={save} />
        <RolesCard roles={data.settings.roles} save={save} />
      </div>
      <div className="lg:col-span-5 space-y-6">
        <TimingCard s={data.settings} save={save} />
        <JoinLinkCard link={data.settings.join_link} save={save} />
        {role === 'owner' && <SchedulersCard orgId={orgId} schedulers={data.settings.schedulers || []} save={save} />}
        {role === 'owner' && <PaymentPermsCard orgId={orgId} schedulers={data.settings.schedulers || []} />}
        {role === 'owner' && <AddonStatusCard />}
      </div>
    </div>
  );
};

export default SettingsTab;
